import { createContext, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { lockerService } from '../../../shared/services/lockers/lockersServices';
import { lockerReservationService } from '../../../shared/services/lockers/lockerReservationService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import React from 'react';

export interface Locker {
  id: string;
  name: string;
  number: string;
  is_available: boolean;
  port?: string;
  mac?: string;
  tenantId: string;
  tenantName?: string;
  roomId: string;
  roomName?: string;
  equipmentId: string;
  equipmentName?: string;
  manufacturerId: string;
  manufacturerName?: string;
  portQuantity?: number;
  finalPortQuantity?: number;
  locker_quantity?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface LockerContextData {
  // Listagem
  lockers: Locker[];
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  handleCreate: () => void;
  handleView: (locker: Locker) => void;
  handleCloseError: () => void;
  handleCreateReservation: (locker: Locker) => void;
  openBlockModal: (locker: Locker) => void;
  isBlockModalOpen: boolean;
  closeBlockModal: () => void;
  handleBlockLocker: () => void;
  isBlocking: boolean;
  // Modal de Desbloqueio
  openUnblockModal: (locker: Locker) => void;
  isUnblockModalOpen: boolean;
  closeUnblockModal: () => void;
  handleUnblockLocker: () => void;
  isUnblocking: boolean;
  // Modal de Delete Locker
  isDeleteModalOpen: boolean;
  selectedLocker: Locker | null;
  openDeleteModal: (locker: Locker) => void;
  closeDeleteModal: () => void;
  handleDeleteLocker: () => void;
  isDeleting: boolean;
  
  // Modal de Delete Reserva
  isDeleteReservationModalOpen: boolean;
  selectedReservation: any | null;
  openDeleteReservationModal: (reservation: any) => void;
  closeDeleteReservationModal: () => void;
  handleDeleteReservation: () => void;
  isDeletingReservation: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  lockerData: Locker | null;
  isDataLoading: boolean;
  createLocker: (data: Omit<Locker, 'id'>) => Promise<void>;
  updateLocker: (id: string, data: Partial<Locker>) => Promise<void>;
  isSubmitting: boolean;
}

interface LockerProviderProps {
  children: ReactNode;
}

export const LockerContext = createContext({} as LockerContextData);

export function LockerProvider({ children }: LockerProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  // Verificar se estamos em uma rota que precisa dos dados do locker
  const shouldFetchLockerData = React.useMemo(() => {
    const pathname = location.pathname;
    // Só buscar dados do locker se estivermos em rotas de detalhes ou edição
    // Não buscar em rotas de reserva
    return Boolean(id && 
           (pathname.includes(`/lockers/${id}`) || pathname.includes(`/lockers/${id}/edit`)) &&
           !pathname.includes('/reservation'));
  }, [id, location.pathname]);

  // Query para listar lockers
  const { data: lockers = [], isLoading } = useQuery({
    queryKey: ['lockers'],
    queryFn: async () => {
      try {
        const response = await lockerService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar lockers';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar lockers');
        }
        return [];
      }
    }
  });

  // Query para buscar dados do locker no modo de edição
  const { data: lockerData = null, isLoading: isDataLoading } = useQuery<Locker | null>({
    queryKey: ['locker', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await lockerService.get(id);
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar locker';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar locker');
        }
        return null;
      }
    },
    enabled: shouldFetchLockerData,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLocker, setSelectedLocker] = useState<Locker | null>(null);

  // Estado do modal de delete de reserva
  const [isDeleteReservationModalOpen, setIsDeleteReservationModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any | null>(null);

  // Mutation para deletar locker
  const deleteMutation = useMutation({
    mutationFn: (id: string) => lockerService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers'] });
      navigate('/lockers');
    }
  });

  // Mutation para deletar reserva
  const deleteReservationMutation = useMutation({
    mutationFn: (id: string) => lockerReservationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locker-reservations'] });
      closeDeleteReservationModal();
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao excluir reserva';
        setError(errorMessage);
      } else {
        setError('Erro ao excluir reserva');
      }
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/lockers/new');
  };

  const handleView = (locker: Locker) => {
    navigate(`/lockers/${locker.id}`);
  };

  const handleCreateReservation = (locker: Locker) => {
    navigate(`/lockers/reservation/${locker.id}/new`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (locker: Locker) => {
    setSelectedLocker(locker);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedLocker(null);
  };

  const handleDeleteLocker = () => {
    if (selectedLocker?.id) {
      deleteMutation.mutate(selectedLocker.id);
    }
  };

  // Handlers para modal de delete de reserva
  const openDeleteReservationModal = (reservation: any) => {
    setSelectedReservation(reservation);
    setIsDeleteReservationModalOpen(true);
  };

  const closeDeleteReservationModal = () => {
    setIsDeleteReservationModalOpen(false);
    setSelectedReservation(null);
  };

  const handleDeleteReservation = () => {
    if (selectedReservation?.id) {
      deleteReservationMutation.mutate(selectedReservation.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/lockers/${id}/edit`);
  };

  // Mutations para criar e atualizar lockers
  const createMutation = useMutation({
    mutationFn: (data: Omit<Locker, 'id'>) => lockerService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers'] });
      navigate('/lockers');
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar locker';
        setError(errorMessage);
      } else {
        setError('Erro ao criar locker');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Locker> }) => 
      lockerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers'] });
      queryClient.invalidateQueries({ queryKey: ['locker', id] });
      navigate('/lockers');
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar locker';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar locker');
      }
    }
  });

  const blockMutation = useMutation({
    mutationFn: (id: string) => lockerService.update(id, { is_available: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers'] });
      closeBlockModal();
    }
  });

  const unblockMutation = useMutation({
    mutationFn: (id: string) => lockerService.update(id, { is_available: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lockers'] });
      closeUnblockModal();
    }
  });

  const createLocker = async (data: Omit<Locker, 'id'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateLocker = async (id: string, data: Partial<Locker>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const openBlockModal = (locker: Locker) => {
    setSelectedLocker(locker);
    setIsBlockModalOpen(true);
  };

  const closeBlockModal = () => {
    setIsBlockModalOpen(false);
    setSelectedLocker(null);
  };

  const handleBlockLocker = () => {
    if (selectedLocker?.id) {
       blockMutation.mutate(selectedLocker.id);
    }
  };

  const openUnblockModal = (locker: Locker) => {
    setSelectedLocker(locker);
    setIsUnblockModalOpen(true);
  };

  const closeUnblockModal = () => {
    setIsUnblockModalOpen(false);
    setSelectedLocker(null);
  };

  const handleUnblockLocker = () => {
    if (selectedLocker?.id) {
       unblockMutation.mutate(selectedLocker.id);
    }
  };

  return (
    <LockerContext.Provider
      value={{
        // Listagem
        lockers,
        isLoading,
        error,
        setError,
        handleCreate,
        handleView,
        handleCloseError,
        handleCreateReservation,        
        // Modal de Delete Locker
        isDeleteModalOpen,
        selectedLocker,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteLocker,
        isDeleting: deleteMutation.isPending,
        
        // Modal de Delete Reserva
        isDeleteReservationModalOpen,
        selectedReservation,
        openDeleteReservationModal,
        closeDeleteReservationModal,
        handleDeleteReservation,
        isDeletingReservation: deleteReservationMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        lockerData,
        isDataLoading,
        createLocker,
        updateLocker,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        openBlockModal,
        isBlockModalOpen,
        closeBlockModal,
        handleBlockLocker,
        isBlocking: false,
        openUnblockModal,
        isUnblockModalOpen,
        closeUnblockModal,
        handleUnblockLocker,
        isUnblocking: unblockMutation.isPending
      }}
    >
      {children}
    </LockerContext.Provider>
  );
}
